import { useState } from 'react';
import dayjs from 'dayjs';
import { CalendarIcon, Plus, Upload, Wallet } from 'lucide-react';
import { useParams } from 'react-router';

import { useCategories } from '@/api/category/use-categories.ts';
import { useTransferStats } from '@/api/transfer/use-transfer-stats.ts';
import { type TransfersFilters, useTransfers } from '@/api/transfer/use-transfers.ts';
import { TransferDtoTypeEnum } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import AddTransferModal from '@/features/transfers/add-transfer-modal/add-transfer-modal.tsx';
import ImportTransfersModal from '@/features/transfers/import-transfers-modal/import-transfers-modal.tsx';
import TransferDetailDrawer from '@/features/transfers/transfer-detail-drawer/transfer-detail-drawer.tsx';
import TransferRow from '@/features/transfers/transfer-row/transfer-row.tsx';
import { formatPrice } from '@/helpers/formatters.ts';
import AppContent from '@/layout/app-content';

const defaultFilters = (): TransfersFilters => ({
  dateFrom: dayjs().startOf('month').toDate(),
  dateTo: dayjs().endOf('day').toDate(),
});

const Transfers = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [filters, setFilters] = useState<TransfersFilters>(defaultFilters);
  const { projectId, transferId } = useParams();

  const { data: categories } = useCategories(projectId);
  const { data: transfers, isLoading } = useTransfers(filters);
  const { data: stats } = useTransferStats(filters);

  const updateFilter = (patch: Partial<TransfersFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
  };

  if (!transfers?.length && isLoading) {
    return (
      <AppContent title="Transfers">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">Loading transfers...</div>
          </CardContent>
        </Card>
      </AppContent>
    );
  }

  return (
    <AppContent title="Transfers">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Transfers</CardTitle>
              <CardDescription>Track money movements and split them by category</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsImportModalOpen(true)}
                data-icon="inline-end"
              >
                Import XML
                <Upload className="size-3.5" />
              </Button>
              <Button onClick={() => setIsAddModalOpen(true)} data-icon="inline-end">
                Add Transfer
                <Plus className="size-3.5" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Party</label>
              <Input
                className="w-40"
                placeholder="Search party"
                value={filters.party || ''}
                onChange={(event) => updateFilter({ party: event.target.value || undefined })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Type</label>
              <select
                className="h-7 rounded-md border border-input bg-input/20 dark:bg-input/30 px-2 text-xs"
                value={filters.type || ''}
                onChange={(event) => updateFilter({ type: event.target.value || undefined })}
              >
                <option value="">All types</option>
                <option value={TransferDtoTypeEnum.Debit}>Debit</option>
                <option value={TransferDtoTypeEnum.Credit}>Credit</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Category</label>
              <select
                className="h-7 rounded-md border border-input bg-input/20 dark:bg-input/30 px-2 text-xs"
                value={filters.categoryId || ''}
                onChange={(event) => updateFilter({ categoryId: event.target.value || undefined })}
              >
                <option value="">All categories</option>
                <option value="null">Uncategorized</option>
                {categories?.map((category) => (
                  <option key={category.uuid} value={category.uuid}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Date</label>
              <Popover>
                <PopoverTrigger>
                  <Button variant="outline" className="justify-start px-2.5 font-normal w-48">
                    <CalendarIcon data-icon="inline-start" />
                    {filters.dateFrom ? (
                      <>
                        {dayjs(filters.dateFrom).format('DD.MM.YYYY')}
                        {filters.dateTo && <> - {dayjs(filters.dateTo).format('DD.MM.YYYY')}</>}
                      </>
                    ) : (
                      <span>All</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 gap-0" align="start">
                  <div className="flex flex-wrap px-3 pt-3 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        updateFilter({
                          dateFrom: dayjs().startOf('month').toDate(),
                          dateTo: dayjs().endOf('day').toDate(),
                        })
                      }
                    >
                      This month
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        updateFilter({
                          dateFrom: dayjs().subtract(1, 'month').startOf('month').toDate(),
                          dateTo: dayjs().subtract(1, 'month').endOf('month').toDate(),
                        })
                      }
                    >
                      Last month
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateFilter({ dateFrom: undefined, dateTo: undefined })}
                    >
                      All
                    </Button>
                  </div>
                  <Calendar
                    mode="range"
                    defaultMonth={filters.dateFrom}
                    selected={{ from: filters.dateFrom, to: filters.dateTo }}
                    onSelect={(date) =>
                      updateFilter({
                        dateFrom: date?.from ? dayjs(date.from).startOf('day').toDate() : undefined,
                        dateTo: date?.to ? dayjs(date.to).endOf('day').toDate() : undefined,
                      })
                    }
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>
      {stats && (
        <div className="flex gap-3">
          <div className="flex-1 border rounded-lg p-3 bg-red-50/60 border-red-200 dark:bg-red-950/10 dark:border-red-900">
            <div className="text-xs text-muted-foreground">Debits</div>
            <div className="text-lg font-semibold text-red-600 dark:text-red-500">
              {formatPrice(-stats.totalDebit)}
            </div>
          </div>
          <div className="flex-1 border rounded-lg p-3 bg-green-50/60 border-green-200 dark:bg-green-950/10 dark:border-green-900">
            <div className="text-xs text-muted-foreground">Credits</div>
            <div className="text-lg font-semibold text-green-600 dark:text-green-500">
              {formatPrice(stats.totalCredit)}
            </div>
          </div>
          <div className="flex-1 border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">Net</div>
            <div
              className={
                stats.totalCredit - stats.totalDebit >= 0
                  ? 'text-lg font-semibold text-green-600 dark:text-green-500'
                  : 'text-lg font-semibold text-red-600 dark:text-red-500'
              }
            >
              {formatPrice(stats.totalCredit - stats.totalDebit)}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2">
        <h2 className="text-base mb-1">All Transfers</h2>
        {transfers?.map((transfer) => (
          <TransferRow key={transfer.uuid} transfer={transfer} />
        ))}
        {!transfers?.length && (
          <div className="text-center py-8">
            <Wallet className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <CardTitle className="text-lg mb-2">No transfers found</CardTitle>
            <CardDescription>Add your first transfer or adjust the filters above.</CardDescription>
          </div>
        )}
      </div>
      {isAddModalOpen && <AddTransferModal onClose={() => setIsAddModalOpen(false)} />}
      {isImportModalOpen && <ImportTransfersModal onClose={() => setIsImportModalOpen(false)} />}
      {transferId && <TransferDetailDrawer transferId={transferId} />}
    </AppContent>
  );
};

export default Transfers;
